/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { ErrorHandlerService, hd } from '@nx-fe/services';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

export class User {
  constructor(public firstname: string, public lastname: string, public selected: boolean = false) {}
}

@Component({
  selector: 'xs-search-and-pick-v2',
  templateUrl: './search-and-pick.component.html',
  styleUrls: ['./search-and-pick.component.scss'],
})
export class SearchAndPickV2Component<T> implements OnInit, OnChanges, OnDestroy {
  searchForm: FormGroup;
  inputControl = new FormControl();
  @ViewChild(MatInput, { static: false }) textInput: MatInput;
  @ViewChild(MatAutocomplete, { static: false }) auto: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger, { static: false }) trigger: MatAutocompleteTrigger;
  @ViewChild('autoCompleteInput', { read: MatAutocompleteTrigger })
  autoComplete: MatAutocompleteTrigger;

  @Input() itemTemplate: TemplateRef<T>;
  @Input() customFilter: (items: T[]) => T[] = undefined;
  @Input('placeholder') placeholder = '';
  @Input('minimumFilterLength') minimumFilterLength: number;
  @Input('searchResource') searchResource: hd.IResourceClass<T>;
  @Input('selectedItem') selectedItem: T;
  @Input('queryParameters') queryParameters: any;
  @Input('excludeList') excludeList: any[];
  @Input('excludeSearchParameter') excludeSearchParameter: any = 'excludeIds';
  @Input('idField') idField = 'ticketId';
  @Input('showSearchAndCancelIcons') showSearchAndCancelIcons = false;
  @Input('disabled') disabled = false;
  @Output('onPick') onPick = new EventEmitter<T>();
  @Output('hasSearched') hasSearched = new EventEmitter<boolean>();
  @Input('itemTemplateName') itemTemplateName = 'None'; // This property need to be set if you use this component in the AngularJS.
  @Input('preventClearOnSelect') preventClearOnSelect = false;
  @Input('toolDisabled') disableTooltip = false;

  @Input() set clearTextOnLoad(value: boolean) {
    if (value) {
      this.clearSearch();
      this.setFocus();
    }
  }

  @Input() set clearSearchResults(value: boolean) {
    if (value) {
      this.trigger.closePanel();
      this.clearSearch();
    }
  }

  @Input() set clearSearchIcon(value: boolean) {
    if (value) {
      this.removeSearchIcon();
    }
  }

  filteredItems: Observable<T[]>;
  lastFilter = '';
  public searchFilter = '';
  noResultsFound: boolean = false;
  isSearchIconDisplay = true;

  searchSubscription: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private errorHandlerService: ErrorHandlerService,
    private elRef: ElementRef
  ) {
    this.filteredItems = this.searchSubject.asObservable();
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    window.removeEventListener('scroll', this.scrollEvent, true);
  }

  ngOnChanges() {
    if (this.searchForm) {
      if (this.disabled) {
        this.clearSearch();
        if (this.searchForm.enabled) {
          this.searchForm.disable();
        }
      } else {
        if (!this.searchForm.enabled) {
          this.searchForm.enable();
        }
      }
    }
  }

  ngOnInit() {
    this.scrollEvent = this.scrollEvent.bind(this);
    window.addEventListener('scroll', this.scrollEvent, true);
    this.searchForm = this.formBuilder.group({
      inputControl: null,
    });

    this.searchSubscription = this.searchForm
      .get('inputControl')
      .valueChanges.pipe(debounceTime(500))
      .subscribe(value => {
        this.filterChanged(value);
      });
  }

  scrollEvent() {
    if (this.autoComplete && this.autoComplete.panelOpen) this.autoComplete.updatePosition();
  }

  private searchSubject = new Subject<T[]>();
  public filterChanged = (filter: string): Observable<T[]> => {
    const searchSubject = this.searchSubject;
    if (!filter) {
      this.searchSubject.next([]);
    }
    if (this.minimumFilterLength === undefined) {
      // default if not specified in markup
      this.minimumFilterLength = 1;
    }

    if (this.minimumFilterLength === 0 || (filter && filter.length >= this.minimumFilterLength)) {
      // Add the IDs of the things to exclude from the search results
      const excludeIds = this.getExcludeIds();
      const searchParams = { searchText: filter };

      if (this.queryParameters) {
        for (const key in this.queryParameters) {
          if (this.queryParameters.hasOwnProperty(key)) {
            searchParams[key] = this.queryParameters[key];
          }
        }
      }

      if (excludeIds) {
        searchParams[this.excludeSearchParameter] = excludeIds.toString();
      } else {
        searchParams[this.excludeSearchParameter] = null;
      }

      this.searchResource.query(
        searchParams,
        (searchResults: any[], responseHeaders?: any) => {
          const warningMessage: string = responseHeaders ? responseHeaders('sa-warning-message') : null;
          if (warningMessage) {
            const warning: hd.IError = {
              error_description: warningMessage,
            };
            this.errorHandlerService.setError(warning);
          }

          if (this.customFilter !== undefined) {
            searchResults = this.customFilter(searchResults);
          }

          searchSubject.next(searchResults);
          this.hasSearched.emit(true);
          if (filter !== this.lastFilter) {
            this.lastFilter = filter;
          }

          if (!searchResults.length || searchResults.length === 0) {
            this.noResultsFound = true;
          }
        },
        (error: any) => {
          throw new Error('search failed: ' + error);
        }
      );
    }
    return this.filteredItems;
  };

  /** Searches the exclude list and returns a list of item IDs to exclude */
  public getExcludeIds = (): (number | string)[] => {
    let excludeIds;
    const excludeList = this.excludeList;
    if (excludeList && excludeList.length > 0) {
      excludeIds = [];
      excludeList.forEach(excludeItem => {
        if (!!excludeItem) {
          if (typeof excludeItem === 'string') {
            excludeIds.push(excludeItem);
          } else if (typeof excludeItem === 'number') {
            excludeIds.push(excludeItem);
          } else if (excludeItem[this.idField]) {
            excludeIds.push(excludeItem[this.idField]);
          }
        }
      }, this);
    }
    return excludeIds;
  };

  public getGlyphicon(selection: any) {
    let result = '';
    switch (selection.status) {
      case 'open':
        result = 'hd-icon hd-icon-open text-status-open status-icon-open';
        break;
      case 'pending':
        result = 'hd-icon hd-icon-more status-icon-pending';
        break;
      case 'resolved':
        result = 'hd-icon hd-icon-accept status-icon-resolved';
        break;
    }

    return result;
  }

  displayWrapped = (value: any) => (!!value ? this.displayFn(value.item, undefined) : '');

  displayFn = (value: T[] | T, fieldName: string): string => {
    if (this.searchInputText.value === '') {
      return;
    }
    if (fieldName === undefined) {
      fieldName = this.idField;
    }
    const display = (item: T) => {
      if (!item) {
        return '';
      }
      if (item[fieldName]) {
        return item[fieldName];
      }

      return item.toString();
    };

    let displayValue: string;

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (index === 0) {
          displayValue = display(item);
        } else {
          displayValue += ', ' + display(item);
        }
      });
    } else {
      displayValue = display(value);
    }

    return displayValue;
  };

  optionSelected(event: Event, selection: T) {
    this.selectItem(selection);
    if (!this.preventClearOnSelect) {
      this.searchFilter = '';
    }
  }

  private selectItem(selection: T) {
    this.searchFilter = this.displayFn(selection, this.idField);
    this.selectedItem = selection;
    this.onPick.emit(selection);
  }

  public clearSearch = (): void => {
    if (this.searchInputText) {
      this.searchInputText.setValue('');
      this.searchSubject.next([]);
      this.noResultsFound = false;
    }
  };

  public setFocus = () => {
    const searchInputElement = this.elRef.nativeElement.querySelector('.searchInput');
    if (searchInputElement) {
      this.removeSearchIcon();
      setTimeout(() => {
        searchInputElement.focus();
      });
    }
  };

  public removeSearchIcon() {
    if (this.showSearchAndCancelIcons) {
      this.isSearchIconDisplay = false;
    }
  }

  public get searchInputText() {
    return this.searchForm ? this.searchForm.get('inputControl') : null;
  }
}
